import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SystemSettings {
  impersonation_notifications_enabled: boolean;
  role_change_email_enabled: boolean;
  admin_action_email_enabled: boolean;
  broadcast_email_enabled: boolean;
  user_deletion_enabled: boolean;
  log_cleanup_enabled: boolean;
}

const DEFAULTS: SystemSettings = {
  impersonation_notifications_enabled: true,
  role_change_email_enabled: true,
  admin_action_email_enabled: true,
  broadcast_email_enabled: true,
  user_deletion_enabled: true,
  log_cleanup_enabled: true,
};

export function useSystemSettings() {
  const [settings, setSettings] = useState<SystemSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("system_settings")
        .select("key, value");

      if (error) throw error;

      const parsed = { ...DEFAULTS };
      for (const row of data ?? []) {
        const key = row.key as keyof SystemSettings;
        if (key in parsed) {
          parsed[key] = row.value === true || row.value === "true";
        }
      }
      setSettings(parsed);
    } catch (err) {
      console.error("Error fetching system settings:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSetting = useCallback(async (key: keyof SystemSettings, value: boolean) => {
    // Optimistic update
    setSettings(prev => ({ ...prev, [key]: value }));

    const { error } = await supabase
      .from("system_settings")
      .update({ value: value as unknown as never, updated_at: new Date().toISOString() })
      .eq("key", key);

    if (error) {
      console.error("Error updating setting:", error);
      // Revert
      setSettings(prev => ({ ...prev, [key]: !value }));
      throw error;
    }
  }, []);

  return { settings, loading, updateSetting, refetch: fetchSettings };
}
